import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { addUser, getUserById, updateUser } from "../../services/userService";

export const AddUserLayer = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  // Configuration for 1-second toasts
  const toastConfig = { autoClose: 1000 };

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    mobile: "",
    password: "",
    my_refer: "",
    role: "agent",
    status: 1,
    remark: "",
  });

  useEffect(() => {
    if (id) loadUser();
  }, [id]);

  const loadUser = async () => {
    try {
      const res = await getUserById(id);
      if (res.status) {
        setFormData({
          name: res.data.name || "",
          email: res.data.email || "",
          mobile: res.data.mobile || "",
          password: "",
          my_refer: res.data.my_refer || "",
          role: res.data.role || "agent",
          status: res.data.status ?? 1,
          remark: res.data.remark || "",
        });
      }
    } catch {
      toast.error("Failed to load user", toastConfig);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setErrors({ ...errors, [e.target.name]: "" });
  };

  const validate = () => {
    const err = {};

    if (!formData.name.trim()) err.name = "Name required";
    if (!formData.email.trim()) err.email = "Email required";
    if (!/^[0-9]{10}$/.test(formData.mobile))
      err.mobile = "Mobile must be 10 digits";

    if (!id && formData.password.length < 6)
      err.password = "Min 6 characters";

    if (Number(formData.status) === 0 && !formData.remark.trim()) {
      err.remark = "Remark required when user is inactive";
    }

    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const res = id
        ? await updateUser(id, formData)
        : await addUser(formData);

      if (res.status === false) {
        toast.error(res.msg || "Operation failed", toastConfig);
      } else {
        toast.success(id ? "User updated" : "User added", toastConfig);
        // Delay navigation slightly so user sees the success toast
        setTimeout(() => {
          navigate("/users-list");
        }, 500);
      }
    } catch {
      toast.error("Server error", toastConfig);
    }
    setLoading(false);
  };

  return (
    <div className="container-fluid my-3">
      <div className="card shadow-sm">
        <div className="card-body">
          <h5 className="mb-4">{id ? "Update User" : "Add User"}</h5>

          <form onSubmit={handleSubmit}>
            <div className="row">
              {/* NAME */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">
                  Full Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="name"
                  placeholder="Enter full name"
                  value={formData.name}
                  onChange={handleChange}
                />
                {errors.name && (
                  <small className="text-danger">{errors.name}</small>
                )}
              </div>

              {/* EMAIL */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">
                  Email Address <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="email"
                  placeholder="Enter email address"
                  value={formData.email}
                  onChange={handleChange}
                />
                {errors.email && (
                  <small className="text-danger">{errors.email}</small>
                )}
              </div>

              {/* MOBILE */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">
                  Mobile Number <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  name="mobile"
                  placeholder="Enter 10 digit mobile"
                  value={formData.mobile}
                  onChange={handleChange}
                />
                {errors.mobile && (
                  <small className="text-danger">{errors.mobile}</small>
                )}
              </div>

              {/* PASSWORD */}
              {!id && (
                <div className="col-12 col-md-4 mb-3">
                  <label className="d-flex">
                    Password <span className="text-danger">*</span>
                  </label>
                  <input
                    type="password"
                    className="form-control"
                    name="password"
                    placeholder="Enter password (min 6 characters)"
                    value={formData.password}
                    onChange={handleChange}
                  />
                  {errors.password && (
                    <small className="text-danger">{errors.password}</small>
                  )}
                </div>
              )}

              {/* REFERRAL */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">Referral Code</label>
                <input
                  className="form-control"
                  name="my_refer"
                  placeholder="Enter referral code"
                  value={formData.my_refer}
                  onChange={handleChange}
                />
              </div>

              {/* ROLE */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">
                  Role <span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="role"
                  value={formData.role}
                  onChange={handleChange}
                >
                  <option value="agent">Agent</option>
                  <option value="user">User</option>
                </select>
              </div>

              {/* STATUS */}
              <div className="col-12 col-md-4 mb-3">
                <label className="d-flex">
                  Status<span className="text-danger">*</span>
                </label>
                <select
                  className="form-select"
                  name="status"
                  value={formData.status}
                  onChange={handleChange}
                >
                  <option value={1}>Active</option>
                  <option value={0}>Inactive</option>
                </select>
              </div>

              {/* REMARK - SHOW ONLY WHEN INACTIVE */}
              {Number(formData.status) === 0 && (
                <div className="col-12 mb-4">
                  <label className="d-flex">
                    Remark <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="remark"
                    rows="3"
                    placeholder="Enter reason for inactive user"
                    value={formData.remark}
                    onChange={handleChange}
                  />
                  {errors.remark && (
                    <small className="text-danger">{errors.remark}</small>
                  )}
                </div>
              )}
            </div>

            <div className="d-flex justify-content-end">
              <button
                type="submit"
                className="btn btn-primary px-5"
                disabled={loading}
              >
                {loading
                  ? "Please wait..."
                  : id
                  ? "Update User"
                  : "Add User"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};